/**
 * One delivery partner, in full.
 *
 * The twin of the pharmacy console: everything about a partner — deliveries,
 * profile, bank, KYC, where they have been, their wallet and the audit trail —
 * on one page instead of scattered across modals on a directory screen.
 *
 * The two switches ops reaches for most sit in the header rather than inside a
 * tab, and both write an audited reason when they turn something off.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Button,
  Skeleton,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  MdArrowBack,
  MdContentCopy,
  MdPowerSettingsNew,
  MdDelete,
} from "react-icons/md";

import { Surface, StatusChip, ConfirmDialog } from "components/ui";
import { T, inr } from "theme/tokens";

import * as api from "./api";
import OverviewTab from "./tabs/OverviewTab";
import OrdersTab from "./tabs/OrdersTab";
import ProfileTab from "./tabs/ProfileTab";
import BankTab from "./tabs/BankTab";
import DocumentsTab from "./tabs/DocumentsTab";
import LocationTab from "./tabs/LocationTab";
import WalletTab from "./tabs/WalletTab";
import ActivityTab from "./tabs/ActivityTab";

const TABS = [
  { key: "overview", label: "Overview", Component: OverviewTab },
  { key: "orders", label: "Deliveries", Component: OrdersTab },
  { key: "profile", label: "Profile", Component: ProfileTab },
  { key: "bank", label: "Bank", Component: BankTab },
  { key: "documents", label: "Documents", Component: DocumentsTab },
  { key: "location", label: "Location", Component: LocationTab },
  { key: "wallet", label: "Wallet", Component: WalletTab },
  { key: "activity", label: "Activity", Component: ActivityTab },
];

const DUTY = {
  onDuty: { label: "On duty", tone: T.success },
  offDuty: { label: "Off duty", tone: T.secondary },
  unverified: { label: "Unverified", tone: T.warning },
};

export default function DeliveryPartner() {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState("");

  // Turning order-taking off stops the partner receiving work, so it is
  // confirmed with a reason rather than fired straight from the switch.
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const [dialog, setDialog] = useState({ kind: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.getPartner(partnerId);
      setPartner(data.partner);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load this delivery partner."
      );
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVerification = async (next, reason) => {
    await api.setVerification(partnerId, next, reason);
    await load();
    setToast(next ? "Partner approved." : "Partner un-approved.");
  };

  const handleTakingOrders = async (next, reason) => {
    await api.setTakingOrders(partnerId, next, reason);
    await load();
    setToast(next ? "Now accepting deliveries." : "Order-taking paused.");
  };

  const copyId = () => {
    navigator.clipboard?.writeText(partner?.partnerId || partnerId);
    setToast("Partner ID copied.");
  };

  if (loading && !partner) {
    return (
      <Box sx={{ pt: 5 }}>
        <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3, mb: 2 }} />
        <Skeleton variant="rounded" height={480} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error && !partner) {
    return (
      <Box sx={{ pt: 5 }}>
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  const ActiveTab = TABS[tab].Component;
  const duty = DUTY[partner?.dutyState] || DUTY.unverified;

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Surface sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Button
              size="small"
              onClick={() => navigate("/admin/delivery-partners")}
              sx={{ minWidth: 0, color: T.muted, mt: 0.3 }}
            >
              <MdArrowBack size={20} />
            </Button>

            <Box>
              <Typography
                sx={{ fontSize: "1.25rem", fontWeight: 700, color: T.ink, lineHeight: 1.2 }}
              >
                {partner?.fullname || "Unnamed partner"}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
                <Typography
                  sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: T.muted }}
                >
                  {partner?.partnerId}
                </Typography>
                <Tooltip title="Copy partner ID">
                  <Button
                    size="small"
                    onClick={copyId}
                    sx={{ minWidth: 0, p: 0.3, color: T.subtle }}
                  >
                    <MdContentCopy size={14} />
                  </Button>
                </Tooltip>
                {partner?.phone && (
                  <Typography sx={{ fontSize: "0.8rem", color: T.muted }}>
                    · {partner.phone}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" spacing={0.8} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <StatusChip tone={duty.tone} label={duty.label} />
                <StatusChip
                  tone={partner?.loginStatus ? T.success : T.subtle}
                  label={partner?.loginStatus ? "Online" : "Offline"}
                />
                <StatusChip
                  tone={partner?.isTakingOrders ? T.info : T.subtle}
                  label={partner?.isTakingOrders ? "Accepting" : "Paused"}
                />
                {!partner?.hasDocuments && (
                  <StatusChip tone={T.error} label="No KYC docs" />
                )}
                {partner?.totalBalance > 0 && (
                  <StatusChip tone={T.warning} label={`${inr(partner.totalBalance)} owed`} />
                )}
              </Stack>
            </Box>
          </Stack>

          {/* The controls ops reaches for most, without opening a tab. */}
          <Stack spacing={0.5} alignItems={{ xs: "flex-start", md: "flex-end" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(partner?.isVerified)}
                  onChange={(e) =>
                    setPendingSwitch({ kind: "verification", next: e.target.checked })
                  }
                />
              }
              label={
                <Typography sx={{ fontSize: "0.82rem", color: T.muted }}>Approved</Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(partner?.isTakingOrders)}
                  onChange={(e) =>
                    setPendingSwitch({ kind: "takingOrders", next: e.target.checked })
                  }
                />
              }
              label={
                <Typography sx={{ fontSize: "0.82rem", color: T.muted }}>
                  Accepting deliveries
                </Typography>
              }
            />

            <Stack direction="row" spacing={0.5}>
              {/* Sign-in is controlled by the partner app, so this only ever
                  goes one way — it is a correction for a crashed app that
                  left someone showing as available. */}
              <Button
                size="small"
                startIcon={<MdPowerSettingsNew size={16} />}
                disabled={!partner?.loginStatus}
                onClick={() => setDialog({ kind: "offline" })}
                sx={{ textTransform: "none", color: T.warning }}
              >
                Force offline
              </Button>
              <Button
                size="small"
                startIcon={<MdDelete size={16} />}
                onClick={() => setDialog({ kind: "delete" })}
                sx={{ textTransform: "none", color: T.error }}
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Surface>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Surface sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              color: T.muted,
              minHeight: 48,
            },
            "& .Mui-selected": { color: `${T.primary} !important` },
            "& .MuiTabs-indicator": { backgroundColor: T.primary, height: 3 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Surface>

      <ActiveTab
        partnerId={partnerId}
        partner={partner}
        onChanged={load}
        notify={setToast}
      />

      {/* ── Switch confirmation ────────────────────────────────────────── */}
      <ConfirmDialog
        open={Boolean(pendingSwitch)}
        title={
          pendingSwitch?.kind === "verification"
            ? pendingSwitch?.next
              ? "Approve this partner?"
              : "Un-approve this partner?"
            : pendingSwitch?.next
            ? "Resume order-taking?"
            : "Pause order-taking?"
        }
        description={
          pendingSwitch?.kind === "verification"
            ? pendingSwitch?.next
              ? "They will start receiving deliveries once signed in and accepting."
              : "They will stop receiving deliveries entirely."
            : pendingSwitch?.next
            ? "Deliveries will be routed to them again while they are signed in."
            : "They stay approved, but new deliveries will go elsewhere."
        }
        confirmLabel={pendingSwitch?.next ? "Enable" : "Disable"}
        destructive={pendingSwitch ? !pendingSwitch.next : false}
        // Turning something off is what someone will later ask about.
        requireReason={pendingSwitch ? !pendingSwitch.next : false}
        onConfirm={async (reason) => {
          if (pendingSwitch.kind === "verification") {
            await handleVerification(pendingSwitch.next, reason);
          } else {
            await handleTakingOrders(pendingSwitch.next, reason);
          }
        }}
        onClose={() => setPendingSwitch(null)}
      />

      <ConfirmDialog
        open={dialog.kind === "offline"}
        title="Force this partner offline?"
        description="They will stop being offered deliveries until they sign in again from the app. Use this when a crashed app has left someone showing as available."
        confirmLabel="Force offline"
        requireReason
        onConfirm={async (reason) => {
          await api.forceOffline(partnerId, reason);
          await load();
          setToast("Partner forced offline.");
        }}
        onClose={() => setDialog({ kind: null })}
      />

      <ConfirmDialog
        open={dialog.kind === "delete"}
        title="Delete this partner?"
        description={
          partner?.totalBalance > 0
            ? `${inr(partner.totalBalance)} is still owed — the server will refuse this until the balance is settled. Deletion is permanent; the record is written to the audit log first.`
            : "This is permanent. The record is written to the audit log first, so it can be reconstructed and explained afterwards."
        }
        confirmLabel="Delete"
        destructive
        requireReason
        onConfirm={async (reason) => {
          await api.deletePartner(partnerId, reason);
          navigate("/admin/delivery-partners");
        }}
        onClose={() => setDialog({ kind: null })}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
}
